import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Check, X } from "lucide-react";

const LANG_LABEL = { en: "English", hi: "हिंदी", mr: "मराठी" };

export default function ReportView() {
  const { id } = useParams();
  const [insp, setInsp] = useState(null);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    api.get(`/inspections/${id}`).then((a) => {
      setInsp(a.data);
      return api.get("/config/checklist", { params: { property_id: a.data.property_id, room: a.data.room_number } });
    }).then((b) => setSections(b.data.sections));
  }, [id]);

  if (!insp || !sections.length) {
    return (
      <Layout title="Report" subtitle="Loading…">
        <div className="hotel-overline">Loading report…</div>
      </Layout>
    );
  }

  const answerMap = Object.fromEntries(insp.answers.map((a) => [a.item_id, a.answer]));

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 no-print">
        <Link to="/inspections">
          <Button variant="outline" size="sm" className="border-[var(--border-warm)]"><ArrowLeft className="w-4 h-4 mr-1" /> Back to log</Button>
        </Link>
        <Button data-testid="modal-print-pdf-button" onClick={() => window.print()} className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white">
          <Printer className="w-4 h-4 mr-2" /> Export PDF
        </Button>
      </div>

      <div data-testid="inspection-report-modal" className="bg-white border border-[var(--border-warm)] rounded-lg p-8 print-page">
        {/* Letterhead */}
        <div className="flex items-start justify-between border-b border-[var(--border-warm)] pb-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-md bg-[var(--navy)] text-[var(--gold)] font-display font-bold text-3xl flex items-center justify-center border border-[var(--gold)]/50">T</div>
            <div>
              <div className="hotel-overline">Tamanna Hotels Group</div>
              <div className="font-display text-3xl text-[var(--navy)] leading-tight">{insp.property_name}</div>
              <div className="text-sm text-[var(--ink-2)]">Housekeeping Room Cleaning Checklist Report</div>
            </div>
          </div>
          <div className="text-right">
            <div className="hotel-overline">Filled At (24h)</div>
            <div data-testid="report-timestamp" className="font-mono-hotel text-lg text-[var(--navy)] font-semibold">{fmtDateTime(insp.created_at)}</div>
            <span className="inline-block mt-1 px-3 py-0.5 rounded-full text-xs uppercase tracking-wider font-semibold bg-[var(--navy)] text-white">
              {insp.service_type === "checkout" ? "Check-out Room" : "Service Room"}
            </span>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
          <div><div className="hotel-overline">Room</div><div className="font-mono-hotel text-lg text-[var(--navy)]">{insp.room_number}</div></div>
          <div><div className="hotel-overline">Guest</div><div className="font-semibold text-[var(--navy)]">{insp.guest_name}</div><div className="text-xs text-[var(--ink-2)]">{insp.guest_count} guest{insp.guest_count > 1 ? "s" : ""}</div></div>
          <div><div className="hotel-overline">Supervisor</div><div className="font-semibold text-[var(--navy)]">{insp.supervisor_name}</div><div className="text-xs text-[var(--ink-2)]">{insp.shift}</div></div>
          <div><div className="hotel-overline">Language Used</div><div className="text-[var(--navy)]">{LANG_LABEL[insp.language] || insp.language}</div><div className="text-xs text-[var(--ink-2)] font-mono-hotel">{insp.answered_count}/{insp.total_items} items</div></div>
        </div>

        {/* Detailed items */}
        {sections.map((sec) => {
          const items = sec.items.filter((it) => answerMap[it.id] !== undefined);
          if (!items.length) return null;
          return (
            <div key={sec.id} className="mb-6">
              <div className="hotel-overline">{sec.title}</div>
              <div className="gold-underline my-2" />
              <ul className="space-y-2">
                {items.map((it) => {
                  const ans = answerMap[it.id];
                  return (
                    <li key={it.id} className="flex items-start gap-3 text-sm">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        ans === true ? "bg-[var(--success-bg)] text-[var(--success)]" : "bg-[var(--danger-bg)] text-[var(--danger)]"
                      }`}>
                        {ans === true ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </div>
                      <div className="flex-1">
                        <div className="text-[var(--ink)]">{it.text_en}</div>
                        <div className="text-xs font-mono-hotel text-[var(--muted)]">{it.code}{it.room_number ? ` · Room ${it.room_number}` : ""}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {insp.remarks && (
          <div className="mb-6">
            <div className="hotel-overline">Supervisor remarks</div>
            <div className="gold-underline my-2" />
            <p className="text-sm text-[var(--ink)] whitespace-pre-wrap">{insp.remarks}</p>
          </div>
        )}

        {/* Signoff */}
        <div className="border-t border-[var(--border-warm)] pt-6 mt-8 flex items-end justify-between">
          <div>
            <div className="w-60 h-8 border-b border-[var(--navy)]"></div>
            <div className="hotel-overline mt-2">Supervisor Signature</div>
            <div className="text-sm text-[var(--ink-2)]">{insp.supervisor_name}</div>
          </div>
          <div className="text-right">
            <div className="w-60 h-8 border-b border-[var(--navy)]"></div>
            <div className="hotel-overline mt-2">Management Signoff</div>
            <div className="text-sm text-[var(--ink-2)]">{insp.property_name}</div>
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-[var(--muted)] font-mono-hotel">
          Report ID: {insp.id}
        </div>
      </div>
    </Layout>
  );
}
